import type { ChangeEvent } from "react";

import { startCase } from "lodash";
import moment from "moment";

import { Input } from "@alpacahq/ui/old";

import { POSITION_INTENT_OPTIONS } from "~/v2/constants/optionOrder";
import { PositionIntentSelector } from "../../shared/option/PositionIntentSelector";
import type { CombinedLeg } from "../utils";

interface LegCardProps {
  leg: CombinedLeg;
  orderType: "market" | "limit";
  onPositionIntentChange: (
    symbol: string,
    positionIntent: CombinedLeg["form"]["positionIntent"],
  ) => void;
  onRatioQtyChange: (symbol: string, ratioQty: number) => void;
  onUpdateLegType: (symbol: string, type: string) => void;
  onRemoveLeg: (symbol: string) => void;
}

export const LegCard = ({
  leg,
  orderType,
  onPositionIntentChange,
  onRatioQtyChange,
  onUpdateLegType,
  onRemoveLeg,
}: LegCardProps) => {
  const {
    data: { contract, quoteSide },
    form: { positionIntent, ratioQty, symbol },
  } = leg;

  const displayPrice = (
    orderType === "limit"
      ? ((contract.snapshot?.latestQuote?.bp ?? 0) +
          (contract.snapshot?.latestQuote?.ap ?? 0)) /
        2
      : quoteSide === "ask"
        ? (contract.snapshot?.latestQuote?.ap ?? 0)
        : (contract.snapshot?.latestQuote?.bp ?? 0)
  ).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const expirationDate = moment(contract.expiration_date).format("MM/DD/YY");

  return (
    <div>
      <div className="flex items-center justify-between">
        <PositionIntentSelector
          value={positionIntent}
          onChange={(intent) => {
            onPositionIntentChange(symbol, intent);
            onUpdateLegType(
              contract.symbol,
              intent.includes("sell") ? "bid" : "ask",
            );
          }}
          options={POSITION_INTENT_OPTIONS}
        />
        <button
          type="button"
          onClick={() => onRemoveLeg(contract.symbol)}
          aria-label="Remove contract"
          className="cursor-pointer border-none bg-transparent p-0 text-gray-400 hover:text-gray-600"
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M12.5 3.5L3.5 12.5M3.5 3.5L12.5 12.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="mt-1 grid grid-cols-[40px_1fr_auto] items-center gap-3">
        <div>
          <label htmlFor={`${symbol}-ratio-qty`} className="sr-only">
            Ratio Qty
          </label>
          <Input
            id={`${symbol}-ratio-qty`}
            type="number"
            min="0"
            step="1"
            className="bg-white p-0 text-center"
            value={Number.isNaN(ratioQty) ? "" : ratioQty}
            onChange={({
              target: { value },
            }: ChangeEvent<HTMLInputElement>) => {
              const newRatio =
                value == null || value.trim() === "" ? NaN : Number(value);
              onRatioQtyChange(contract.symbol, newRatio);
            }}
          />
        </div>

        <div className="flex min-w-0 flex-col">
          <span className="whitespace-nowrap font-semibold text-gray-700 text-sm leading-5">
            ${contract.strike_price} {startCase(contract.type)}
          </span>
          <span className="whitespace-nowrap text-gray-500 text-xs">
            {expirationDate}
          </span>
        </div>

        <span className="whitespace-nowrap font-mono text-gray-800 text-sm leading-5">
          {positionIntent.includes("sell") && "-"}${displayPrice}
        </span>
      </div>
    </div>
  );
};
